// Event Inbox view — /api/v2/events DB-backed read.
// Sprint 3 PR-E2: Backend /events response artık camelCase (route boundary'de
// map ediliyor). UI snake_case mapping borcu kalktı; doğrudan EventDto tüketilir.
// - apiGetJson (401 redirect + 5xx toast)
// - Basit filtreler: sourceApp, type, status, limit
// - Payload preview <details> ile inline (drawer PR-D2'de)
// - Pure renderEventInboxHtml() helper test edilebilir

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

// Backend DTO — /api/v2/events route boundary'sinde map edilmiş camelCase shape.
export interface EventDto {
  id: string;
  tenantId: string;
  sourceApp: string;
  type: string;
  entityType: string;
  entityId: string;
  payload: unknown;
  priority: string;
  riskLevel: string;
  status: string;
  createdAt: string;
  processedAt: string | null;
}

export interface EventInboxFilters {
  sourceApp?: string;
  type?: string;
  status?: string;
  limit?: number;
}

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; events: EventDto[]; filters: EventInboxFilters }
  | { kind: 'error'; message: string; filters: EventInboxFilters };

let state: ViewState = { kind: 'idle' };
let filters: EventInboxFilters = { limit: 50 };

export async function loadEventInbox(): Promise<void> {
  state = { kind: 'loading' };
  const result = await apiGetJson<{ ok: true; events: EventDto[] }>(
    '/api/v2/events',
    {
      sourceApp: filters.sourceApp,
      type: filters.type,
      status: filters.status,
      limit: filters.limit,
    },
  );
  if (result.ok) {
    state = {
      kind: 'ready',
      events: result.data.events ?? [],
      filters: { ...filters },
    };
  } else {
    state = { kind: 'error', message: result.message ?? result.error, filters: { ...filters } };
  }
}

export function renderEventInbox(): void {
  const root = document.getElementById('view-event-inbox');
  if (!root) return;
  root.innerHTML = renderEventInboxHtml(state);
  attachFilterHandlers();
}

// Pure render — test edilebilir.
export function renderEventInboxHtml(s: ViewState): string {
  const f = s.kind === 'ready' || s.kind === 'error' ? s.filters : filters;
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Event Inbox</div>
        <div class="view-sub">Tüm uygulamalardan gelen merkezi event akışı</div>
      </div>
    </div>
    <div class="rt-card" style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
      ${filterInput('sourceApp', 'Source app', f.sourceApp)}
      ${filterInput('type', 'Type', f.type)}
      ${filterSelect('status', 'Status', f.status, ['received','routed','processing','completed','failed','approval_required'])}
      ${filterSelect('limit', 'Limit', f.limit ? String(f.limit) : '50', ['10','25','50','100','200'])}
      <button id="evt-apply" class="btn-primary" style="padding:6px 12px;">Filtrele</button>
    </div>`;
  if (s.kind === 'idle' || s.kind === 'loading') {
    return `${header}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  }
  if (s.kind === 'error') {
    return `${header}<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(s.message)}</div></div>`;
  }
  const events = s.events;
  return `${header}
    <div class="rt-card">
      <div class="rt-card-title">Eventler (${events.length})</div>
      ${events.length === 0
        ? '<div class="empty-state"><div class="empty-icon">📭</div><div class="empty-title">Eşleşen event yok</div></div>'
        : `<table style="width:100%;font-size:13px;">
            <thead><tr>
              <th style="text-align:left;">ID</th>
              <th style="text-align:left;">Tenant</th>
              <th style="text-align:left;">Source</th>
              <th style="text-align:left;">Type</th>
              <th style="text-align:left;">Risk</th>
              <th style="text-align:left;">Status</th>
              <th style="text-align:left;">Created</th>
            </tr></thead>
            <tbody>
              ${events.map((e) => `
                <tr>
                  <td>${escHtml(e.id.slice(0, 8))}</td>
                  <td>${escHtml(e.tenantId)}</td>
                  <td><code style="font-size:11px;">${escHtml(e.sourceApp)}</code></td>
                  <td>${escHtml(e.type)}</td>
                  <td>${escHtml(e.riskLevel)}</td>
                  <td>${escHtml(e.status)}</td>
                  <td style="color:var(--muted);font-size:12px;">${escHtml(e.createdAt)}</td>
                </tr>
                <tr><td colspan="7" style="padding:0 0 12px 0;">
                  <details><summary style="cursor:pointer;font-size:11px;color:var(--muted);">payload</summary>
                    <pre style="background:var(--bg2);padding:8px;border-radius:4px;font-size:11px;white-space:pre-wrap;">${escHtml(JSON.stringify(e.payload, null, 2))}</pre>
                  </details>
                </td></tr>
              `).join('')}
            </tbody>
          </table>`
      }
    </div>`;
}

function filterInput(id: string, label: string, value: string | undefined): string {
  return `<label style="font-size:12px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">
    ${escHtml(label)}
    <input id="evt-f-${id}" class="form-input" value="${escHtml(value ?? '')}" style="width:140px;padding:4px 8px;" />
  </label>`;
}

function filterSelect(id: string, label: string, value: string | undefined, opts: string[]): string {
  return `<label style="font-size:12px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">
    ${escHtml(label)}
    <select id="evt-f-${id}" class="form-select" style="width:140px;padding:4px 8px;">
      <option value="">(any)</option>
      ${opts.map((o) => `<option value="${escHtml(o)}"${o === value ? ' selected' : ''}>${escHtml(o)}</option>`).join('')}
    </select>
  </label>`;
}

function attachFilterHandlers(): void {
  const btn = document.getElementById('evt-apply');
  if (!btn) return;
  btn.addEventListener('click', async () => {
    const sourceApp = (document.getElementById('evt-f-sourceApp') as HTMLInputElement | null)?.value || undefined;
    const type = (document.getElementById('evt-f-type') as HTMLInputElement | null)?.value || undefined;
    const status = (document.getElementById('evt-f-status') as HTMLSelectElement | null)?.value || undefined;
    const limitRaw = (document.getElementById('evt-f-limit') as HTMLSelectElement | null)?.value;
    const limit = limitRaw ? parseInt(limitRaw, 10) : 50;
    filters = { sourceApp, type, status, limit };
    await loadEventInbox();
    renderEventInbox();
  });
}
